/**
 * Tela /templates (TH-53259 / M3.E6.S1.4).
 *
 * Substitui o PageStub registrado em router.tsx até esta subtask.
 * Lista os templates de arte do tenant para que o operador possa abrir
 * o editor (TH-53258 — placeholder até lá) ou criar um novo (também
 * placeholder).
 *
 * Backend: GET /api/templates (TH-53240 / M3.E6.S1.1).
 *
 * TH-55132 (M-cartaz.4): toggle "Todas / Clean / Cartazista" acima da
 * galeria. Sem coluna `family` no schema — usamos o array `tags` do
 * próprio template como fonte:
 *   - `cartazista` → `tags` contém `"cartazista"`
 *   - `clean` → ausência de `"cartazista"` (default-clean, vale para
 *     templates antigos sem tags)
 *   - `all` → todos
 * Persistência via query string (`?family=cartazista`) seguindo o
 * mesmo padrão de TvsViewControls (TH-55138). Filtragem client-side
 * por enquanto; backend filter é trabalho futuro.
 *
 * Decisões — adaptações ao schema real (TemplateRead):
 * - O DoD do prompt mencionava colunas "Slug", "Tipo (enum)" e "Status",
 *   mas o backend NÃO tem `slug`, `type` nem `status` enum. Adaptamos
 *   sem inventar campos:
 *     * "Tipo" = nome da área associada (`area_id` → /api/areas).
 *     * "Status" composto a partir de `is_default` + `active`:
 *         - is_default && active → Badge success "Padrão"
 *         - active             → Badge success "Ativo"
 *         - !active            → Badge muted "Inativo"
 *     * "Identificador" = primeiros 8 chars do UUID em mono (sem slug).
 * - Backend retorna ARRAY direto (não { items, total, ... }) e sem
 *   paginação por query string. Listagem é client-side: GET único +
 *   slice de página em memória. Quando o backend ganhar `?page=` real,
 *   trocamos por server-side sem mudar a UI.
 * - FilterPills por área (em vez de 6 tipos hardcoded). Usa as áreas
 *   reais do tenant; se /api/areas falhar, oculta o filtro silenciosamente.
 *
 * TH-55362: clique na linha agora abre `/templates/:id` via `<Link>`
 * do TanStack Router (era `<a>` + `e.preventDefault()` + `window.location.href`
 * — recarregava a página inteira em vez de navegação SPA). Botão "+
 * Novo template" virou trigger do `NewTemplateDialog`: POST /api/templates
 * com scaffolds neutros, redireciona pro editor (TH-53258) onde o
 * operador refina html/css/ai_prompt.
 *
 * Refs visuais: ProductsPage (TH-53223) — mesmo layout server-pagination
 * (aqui adaptado para client-side enquanto backend não pagina).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { AppLayout } from "@/components/AppLayout";
import { NewTemplateDialog } from "@/components/NewTemplateDialog";
import { RequireAuth } from "@/components/RequireAuth";
import { Badge } from "@/components/ui/Badge";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import {
  FilterPills,
  type FilterPillOption,
} from "@/components/ui/FilterPills";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

interface Template {
  id: string;
  area_id: string | null;
  name: string;
  width: number;
  height: number;
  version: number;
  is_default: boolean;
  active: boolean;
  // `tags` chega do backend (TH-55132). Default `[]` para tolerar
  // respostas de versões antigas durante rollout.
  tags?: string[];
  created_at: string;
  updated_at: string;
}

type TemplateFamily = "all" | "clean" | "cartazista";

interface TemplatesSearchParams {
  family?: TemplateFamily;
}

const FAMILY_OPTIONS: readonly { value: TemplateFamily; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "clean", label: "Clean" },
  { value: "cartazista", label: "Cartazista" },
];

function familyOf(t: Template): TemplateFamily {
  // Sem coluna `family` no schema — derivado de `tags`. Default-clean:
  // templates antigos sem tags aparecem em "Clean" (intuição: o que não
  // é explicitamente "cartazista" é considerado padrão da casa).
  if ((t.tags ?? []).includes("cartazista")) return "cartazista";
  return "clean";
}

interface Area {
  id: string;
  name: string;
  slug: string;
}

interface AreasResponse {
  data: Area[];
  meta: { total: number };
}

const PER_PAGE = 20;
const ALL_AREAS = "__all__";
const NO_AREA = "__none__";

async function fetchTemplates(): Promise<Template[]> {
  const res = await fetch("/api/templates", { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as Template[];
}

async function fetchAreas(): Promise<AreasResponse> {
  // per_page alto: tenant raramente tem >100 áreas; pega tudo de uma vez
  // pra preencher FilterPills + mapa id→name.
  const res = await fetch("/api/areas?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as AreasResponse;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface StatusInfo {
  label: string;
  variant: "success" | "warn" | "destructive" | "muted";
}

function statusFor(t: Template): StatusInfo {
  if (!t.active) return { label: "Inativo", variant: "muted" };
  if (t.is_default) return { label: "Padrão", variant: "success" };
  return { label: "Ativo", variant: "success" };
}

export function TemplatesPage() {
  const { user } = useAuth();
  const canRead =
    user?.role === "super_admin" ||
    user?.role === "tenant_admin" ||
    user?.role === "operator" ||
    user?.role === "catalog_operator";

  // Família vive em query string (`?family=...`). Outros filtros
  // (busca, área) seguem com `useState` por ora — refatorar pra URL
  // é trabalho separado.
  const search = useSearch({ strict: false }) as TemplatesSearchParams;
  const navigate = useNavigate();
  const familyFilter: TemplateFamily =
    search.family === "clean" || search.family === "cartazista"
      ? search.family
      : "all";

  function setFamily(next: TemplateFamily) {
    navigate({
      to: "/templates",
      search: (prev) => ({
        ...prev,
        family: next === "all" ? undefined : next,
      }),
    });
  }

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [areaFilter, setAreaFilter] = useState<string>(ALL_AREAS);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Debounce 300ms — alinha com ProductsPage.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [areaFilter]);

  useEffect(() => {
    setPage(1);
  }, [familyFilter]);

  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: fetchTemplates,
    staleTime: 30_000,
    enabled: canRead,
  });

  const areasQuery = useQuery({
    queryKey: ["areas", "for-templates-filter"],
    queryFn: fetchAreas,
    staleTime: 5 * 60_000,
    enabled: canRead,
    retry: false,
  });

  const allTemplates = templatesQuery.data ?? [];
  const areasList = areasQuery.data?.data ?? [];
  const areaNameById = new Map(areasList.map((a) => [a.id, a.name] as const));

  // Só mostra "Sem área" no filtro se houver pelo menos um template sem área.
  const hasOrphanTemplates = allTemplates.some((t) => !t.area_id);

  const showAreaFilter = areasQuery.isSuccess && areasList.length > 0;

  const areaFilterOptions: readonly FilterPillOption<string>[] = [
    { label: "Todos", value: ALL_AREAS },
    ...areasList.map((a) => ({ label: a.name, value: a.id })),
    ...(hasOrphanTemplates
      ? [{ label: "Sem área", value: NO_AREA } as const]
      : []),
  ];

  // Filtragem client-side: aplica família, busca e filtro de área
  // antes de paginar. Quando o backend ganhar `?family=` real, basta
  // remover este `if (familyFilter !== "all")`.
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return allTemplates.filter((t) => {
      if (familyFilter !== "all" && familyOf(t) !== familyFilter) {
        return false;
      }
      if (areaFilter === NO_AREA) {
        if (t.area_id) return false;
      } else if (areaFilter !== ALL_AREAS) {
        if (t.area_id !== areaFilter) return false;
      }
      if (term && !t.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [allTemplates, q, areaFilter, familyFilter]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const rows = filtered.slice(
    (safePage - 1) * PER_PAGE,
    safePage * PER_PAGE,
  );

  if (user && !canRead) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="OPERAÇÃO / TEMPLATES" title="Acesso negado">
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin,
              operator ou catalog_operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  // TH-55174: botão "Gerar com IA" só aparece para roles que podem
  // disparar geração (super_admin/tenant_admin), espelhando o gate
  // server-side de _check_can_generate em routers/templates.py.
  const canGenerateIA =
    user?.role === "super_admin" || user?.role === "tenant_admin";
  const headerActions = (
    <div className="flex items-center gap-2">
      {canGenerateIA ? (
        <Link
          to="/templates/generate"
          data-testid="generate-with-ai-link"
        >
          <Button variant="ghost" size="sm">
            Gerar com IA
          </Button>
        </Link>
      ) : null}
      <Button
        variant="primary"
        size="sm"
        onClick={() => setNewDialogOpen(true)}
        data-testid="new-template-button"
      >
        + Novo template
      </Button>
    </div>
  );

  const columns: DataTableColumn<Template>[] = [
    {
      key: "id",
      header: "ID",
      mono: true,
      width: "100px",
      cell: (t) => (
        <span className="text-[var(--color-muted-foreground)]">
          {t.id.slice(0, 8)}
        </span>
      ),
    },
    {
      key: "name",
      header: "Nome",
      cell: (t) => (
        // TH-55362: navegação SPA via TanStack Link (antes era <a> +
        // window.location, que recarregava a página inteira). Editor
        // real em TemplateEditPage (TH-53258).
        <Link
          to="/templates/$templateId"
          params={{ templateId: t.id }}
          className="font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] hover:underline"
          data-testid={`template-link-${t.id}`}
        >
          {t.name}
        </Link>
      ),
    },
    {
      key: "area_id",
      header: "Tipo",
      cell: (t) => {
        if (!t.area_id) {
          return <Badge variant="muted">sem área</Badge>;
        }
        const name = areaNameById.get(t.area_id);
        return <Badge variant="muted">{name ?? "—"}</Badge>;
      },
    },
    {
      key: "version",
      header: "Versão",
      mono: true,
      width: "90px",
      cell: (t) => (
        <span className="text-[var(--color-muted-foreground)]">v{t.version}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "110px",
      cell: (t) => {
        const s = statusFor(t);
        return <StatusPill tone={s.variant}>{s.label}</StatusPill>;
      },
    },
    {
      key: "updated_at",
      header: "Atualizado em",
      align: "right",
      mono: true,
      cell: (t) => (
        <span className="text-[var(--color-muted-foreground)]">
          {formatDateTime(t.updated_at)}
        </span>
      ),
    },
  ];

  const subtitle = templatesQuery.data
    ? `${total} ${total === 1 ? "template" : "templates"}`
    : undefined;

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / TEMPLATES"
        title="Templates"
        actions={headerActions}
      >
        <NewTemplateDialog
          open={newDialogOpen}
          onOpenChange={setNewDialogOpen}
          onCreated={(id) => {
            // Invalida a query da listagem pra que o novo template
            // apareça quando o operador voltar; navega pro editor.
            void queryClient.invalidateQueries({ queryKey: ["templates"] });
            void navigate({
              to: "/templates/$templateId",
              params: { templateId: id },
            });
          }}
        />
        <div className="mx-auto max-w-6xl">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Templates de arte usados na geração de imagens para as TVs.
            Use a busca pelo nome ou filtre por área para localizar um
            template.
          </p>

          <div className="mt-6 flex flex-col gap-3">
            <div
              role="group"
              aria-label="Família do template"
              className="inline-flex w-fit rounded-md border border-[var(--color-border)] p-0.5"
            >
              {FAMILY_OPTIONS.map((opt) => {
                const active = familyFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFamily(opt.value)}
                    className={cn(
                      "rounded px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-[var(--color-primary-soft)] text-[var(--color-primary)]"
                        : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="max-w-md">
              <Input
                type="search"
                placeholder="Buscar por nome…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Buscar templates"
              />
            </div>
            {showAreaFilter ? (
              <FilterPills
                options={areaFilterOptions}
                value={areaFilter}
                onChange={setAreaFilter}
              />
            ) : null}
          </div>

          {templatesQuery.error ? (
            <div className="mt-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              erro ao carregar templates
            </div>
          ) : null}

          <div className="mt-4">
            <DataTable<Template>
              columns={columns}
              rows={rows}
              title="Templates"
              subtitle={subtitle}
              rowKey={(t) => t.id}
              emptyMessage={
                templatesQuery.isLoading
                  ? "carregando…"
                  : q.trim() || areaFilter !== ALL_AREAS || familyFilter !== "all"
                    ? "nenhum template bate com o filtro"
                    : "nenhum template cadastrado"
              }
            />
          </div>

          {templatesQuery.data && total > PER_PAGE ? (
            <div className="mt-4">
              <Pagination
                page={safePage}
                perPage={PER_PAGE}
                total={total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
